import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Icon from '@material-ui/core/Icon';
import range from 'lodash/range';
import { uiState } from 'react-redux-ui-state';

import ORTypography from 'components/ORTypography/ORTypography';

const propTypes = {
  addons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  newInstanceAddons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectAddon: PropTypes.func.isRequired,
  deselectAddon: PropTypes.func.isRequired,
  getPrice: PropTypes.func.isRequired,
  openMenu: PropTypes.string.isRequired,
  anchorEl: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  setUiState: PropTypes.func.isRequired,
  priceNeedsUpdating: PropTypes.bool.isRequired,
  priceInfo: PropTypes.instanceOf(Array).isRequired,
};

export function UnstyledAddonCards({
  addons,
  priceInfo,
  newInstanceAddons,
  selectAddon,
  deselectAddon,
  getPrice,
  openMenu,
  anchorEl,
  priceNeedsUpdating,
  classes,
  setUiState,
}) {
  const handleCardIconClick = (addonId, addonName, addonValue, isSelected) => () => {
    if (isSelected) {
      deselectAddon(addonId);
    } else {
      selectAddon(addonId, addonName, addonValue);
    }
    setUiState({ priceNeedsUpdating: true });
  };

  const handleMenuClick = (menuId) => (event) => {
    setUiState({
      openMenu: menuId,
      anchorEl: event.currentTarget,
    });
  };

  const handleClose = () => {
    setUiState({
      openMenu: '',
      anchorEl: {},
    });
  };

  const handleMenuItemClick = (addonId, addonName, addonValue) => () => {
    handleClose();
    selectAddon(addonId, addonName, addonValue);
    setUiState({ priceNeedsUpdating: true });
  };

  function createMenu(addonId, addonName, valueObject) {
    let options;
    const validValueTypes = ['integer', 'string'];

    if (!validValueTypes.includes(valueObject.type)) {
      return null;
    }

    if (valueObject.type === 'integer') {
      options = range(Number(valueObject.minimum), Number(valueObject.maximum) + 1).reverse();
    }
    if (valueObject.type === 'string') {
      options = valueObject.enum;
    }

    return options.map((option) => {
      const isSelected =
        newInstanceAddons.filter((newAddon) => newAddon.value === option).length > 0 ? '1' : '0';
      return (
        <MenuItem
          key={option}
          onClick={handleMenuItemClick(addonId, addonName, option)}
          className={classes.addonMenuItem}
          is-selected={isSelected}
        >
          {option}
        </MenuItem>
      );
    });
  }

  const addonCards = addons
    .filter((addon) => addon.displaySetting === 1)
    .map((addon) => {
      const isSelected =
        newInstanceAddons.filter((newAddon) => newAddon.id === addon.id).length > 0 ? '1' : '0';
      let defaultValue;
      if (addon.value.type === 'boolean') {
        defaultValue = true;
      } else if (addon.value.type === 'string') {
        [defaultValue] = addon.value.enum;
      } else {
        defaultValue = addon.value.default;
      }

      return (
        <div key={addon.id} className={classes.addonCard} is-selected={isSelected}>
          <Icon
            className={classes.addonCardSelectionIndicator}
            onClick={handleCardIconClick(addon.id, addon.name, defaultValue, Number(isSelected))}
            is-selected={isSelected}
          >
            done
          </Icon>

          <div className={classes.addonCardTextSection}>
            <ORTypography variant="h6" className={classes.addonCardHeader}>
              {addon.displayName}
            </ORTypography>
            <ORTypography className={classes.addonCardText}>{addon.description}</ORTypography>
          </div>

          {addon.value.type !== 'boolean' && (
            <React.Fragment>
              <Icon
                className={classes.addonCardSelectionMenuIcon}
                onClick={handleMenuClick(addon.id)}
              >
                more_horiz
              </Icon>

              <Menu
                id={`${addon.id}-menu`}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorEl={anchorEl}
                keepMounted
                open={openMenu === addon.id}
                onClose={handleClose}
              >
                {createMenu(addon.id, addon.name, addon.value)}
              </Menu>
            </React.Fragment>
          )}
        </div>
      );
    });

  if (priceNeedsUpdating) {
    getPrice(...priceInfo, newInstanceAddons);
    setUiState({
      priceNeedsUpdating: false,
    });
  }
  return <React.Fragment>{addonCards}</React.Fragment>;
}

UnstyledAddonCards.propTypes = propTypes;

const styles = (theme) => ({
  addonCard: {
    border: `1px solid ${theme.customColors.black}`,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),

    '&[is-selected="1"]': {
      border: `2px solid ${theme.customColors.teal}`,
    },
  },
  addonCardSelectionIndicator: {
    alignSelf: 'flex-start',
    fontSize: '4rem',
    marginRight: theme.spacing(1),

    '&:hover': {
      cursor: 'pointer',
    },

    '&[is-selected="1"]': {
      color: theme.customColors.teal,
    },
  },
  addonCardSelectionMenuIcon: {
    alignSelf: 'baseline',

    '&:hover': {
      cursor: 'pointer',
    },
  },
  addonMenuItem: {
    '&[is-selected="1"]': {
      backgroundColor: theme.customColors.sky,
    },
  },
  addonCardHeader: {
    fontSize: '1rem',
    color: theme.customColors.black,
  },
  addonCardText: {
    fontSize: '1rem',
    color: theme.customColors.mediumGrey,
  },
});

export const uiConfig = {
  name: 'createInstance',
  state: () => ({
    openMenu: '',
    anchorEl: {},
    priceNeedsUpdating: false,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledAddonCards));
